import { useAction } from 'convex/react';
import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';
import { useEffectiveSerial } from './useEffectiveSerial';

export interface InstalledAppWithStatus {
  _id?: string;
  bundleId: string;
  name?: string;
  iconUrl?: string;
  trackId?: string;
  status: 'allowed' | 'blocked';
  version?: string;
}

const FILTERED_BUNDLE_IDS = [
  'com.apple.AppStore',
  'com.apple.mobilesafari',
  'com.google.chrome.ios',
  'dev.shomri.shomriweb',
  'dev.shomri.ShomriWeb',
];

export const useInstalledAppsWithStatus = (enabled: boolean = false) => {
  const { serial } = useEffectiveSerial();
  const fetchApps = useAction(api.apps.list.allOnDeviceWithStatus);

  const [apps, setApps] = useState<InstalledAppWithStatus[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!serial) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchApps({ serial });
      const filtered = (result as InstalledAppWithStatus[]).filter(
        (app) =>
          !FILTERED_BUNDLE_IDS.includes(app.bundleId) &&
          !app.name?.toLowerCase().includes('shomri web')
      );
      setApps(filtered);
    } catch (e: any) {
      console.error('[useInstalledAppsWithStatus] Error:', e);
      setError(e?.message || 'Failed to fetch installed apps');
    } finally {
      setIsLoading(false);
    }
  }, [serial, fetchApps]);

  useEffect(() => {
    if (enabled) {
      load();
    }
  }, [enabled, load]);

  return { apps, isLoading, error, refetch: load };
};
